import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Eye, EyeOff, Info, Loader2, Lock } from 'lucide-react';
import type { MyDataResponse, MyDataRevealResponse } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { formatDateMedium } from '@/lib/hr';
import { t } from '@/i18n';

const REVEAL_SECONDS = 5 * 60;
const MASK = '••••';

const formatPln = (v: number) =>
  `${v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN`;

/** Dense label→value line used inside the kartoteka zones. */
function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 py-1">
      <span className="text-muted-foreground shrink-0 text-xs">{label}</span>
      <span className="truncate text-right text-sm font-medium" title={value || undefined}>
        {value || '—'}
      </span>
    </div>
  );
}

/** Label-above-value field for long identifiers (PESEL, IBAN). */
function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="truncate font-mono text-sm font-medium" title={value || undefined}>
        {value || '—'}
      </p>
    </div>
  );
}

function ZoneTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground mb-1.5 text-[11px] font-medium tracking-wider uppercase">
      {children}
    </p>
  );
}

/** One entry of the payslip flow strip: small label over a mono value. */
function PayItem({
  label,
  title,
  value,
  emphasis,
}: {
  label: string;
  title?: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-0" title={title}>
      <p className="text-muted-foreground text-[11px]">{label}</p>
      <p
        className={
          emphasis
            ? 'text-primary font-mono text-base font-semibold tabular-nums'
            : 'font-mono text-sm tabular-nums'
        }
      >
        {value}
      </p>
    </div>
  );
}

/** Muted arithmetic sign between payslip entries. */
function PayOp({ children }: { children: string }) {
  return <span className="text-muted-foreground pb-0.5 text-sm select-none">{children}</span>;
}

/**
 * HR "Moje dane": the employee's own kartoteka, read-only — laid out as ONE
 * personnel card (header band, contact/address/employment zones, sensitive
 * band with a payslip-flow strip) instead of stacked sparse cards. Sensitive
 * values arrive only after the PESEL-last-4 reveal and live in memory for
 * 5 minutes.
 */
export function MyDataView({ nip }: { nip: string }) {
  const { myData, revealMyData } = useHrApi();
  const [data, setData] = useState<MyDataResponse | null>(null);
  const [revealed, setRevealed] = useState<MyDataRevealResponse | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [digits, setDigits] = useState('');
  const [busy, setBusy] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let mounted = true;
    myData(nip)
      .then((d) => { if (mounted) setData(d); })
      .catch((error) => {
        if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
      });
    return () => { mounted = false; };
  }, [nip, myData]);

  const hide = useCallback(() => {
    setRevealed(null);
    setSecondsLeft(0);
    if (hideTimer.current) clearInterval(hideTimer.current);
    hideTimer.current = null;
  }, []);

  // Auto-hide: countdown every second; clears on unmount too.
  useEffect(() => () => { if (hideTimer.current) clearInterval(hideTimer.current); }, []);

  const startCountdown = () => {
    setSecondsLeft(REVEAL_SECONDS);
    if (hideTimer.current) clearInterval(hideTimer.current);
    hideTimer.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          hide();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleReveal = async () => {
    if (!/^\d{4}$/.test(digits)) return;
    setBusy(true);
    try {
      const res = await revealMyData(nip, digits);
      setRevealed(res);
      startCountdown();
      setDialogOpen(false);
      setDigits('');
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error((error as Error).message);
        // A failed attempt may have tripped the hard lock — refresh the state.
        myData(nip).then(setData).catch(() => undefined);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Skeleton className="h-96 w-full" />;

  const sexLabel = data.personal.sex === 'male'
    ? t('hr.myData.sexMale')
    : data.personal.sex === 'female' ? t('hr.myData.sexFemale') : null;

  const initials =
    `${data.personal.firstName?.[0] ?? ''}${data.personal.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const identityLine = [
    data.personal.dateOfBirth
      ? t('hr.myData.bornOn', { date: formatDateMedium(data.personal.dateOfBirth) })
      : null,
    sexLabel,
  ].filter(Boolean).join(' · ');
  const cityLine = [data.address.postalCode, data.address.city].filter(Boolean).join(' ') || null;

  const payroll = revealed?.payroll;
  const b = payroll?.breakdown;
  const pay = (v: number | null | undefined) =>
    !revealed ? MASK : v !== null && v !== undefined ? formatPln(v) : '—';

  const idDocument = (() => {
    const type = revealed ? revealed.idDocumentType : data.sensitive.idDocumentType;
    const num = revealed ? revealed.idDocumentNumber : data.sensitive.idDocumentNumberMasked;
    if (!num) return null;
    const typeLabel = type === 'passport' ? t('hr.myData.idPassport') : t('hr.myData.idDowod');
    return `${typeLabel}: ${num}`;
  })();

  return (
    <div className="flex flex-col gap-3">
      <Card className="gap-0 overflow-hidden py-0">
        {/* Header band: who + the employment facts that identify the kartoteka */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b p-4 sm:px-5">
          <div className="bg-primary/10 text-primary flex size-12 shrink-0 items-center justify-center rounded-lg text-lg font-semibold">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg leading-tight font-semibold">
                {data.personal.firstName} {data.personal.lastName}
              </h2>
              {data.employment.jobTitle && <Badge>{data.employment.jobTitle}</Badge>}
              <Badge variant="secondary">
                {data.employment.employmentType
                  ? t(`employees.type.${data.employment.employmentType}`)
                  : t('employees.type.notSet')}
              </Badge>
            </div>
            {identityLine && (
              <p className="text-muted-foreground mt-0.5 text-xs">{identityLine}</p>
            )}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-x-6 gap-y-2">
            <PayItem
              label={t('hr.myData.contractStart')}
              value={
                data.employment.contractStartDate
                  ? formatDateMedium(data.employment.contractStartDate)
                  : '—'
              }
            />
            {data.employment.contractExpiryDate && (
              <PayItem
                label={t('hr.myData.contractExpiry')}
                value={formatDateMedium(data.employment.contractExpiryDate)}
              />
            )}
            <PayItem label={t('hr.myData.fte')} value={String(data.employment.fte)} />
            <PayItem
              label={t('hr.myData.dailyNorm')}
              value={`${data.employment.dailyNormHours} h`}
            />
          </div>
        </div>

        {/* Contact | Address | Organisation zones */}
        <div className="divide-border grid divide-y border-b sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-3 lg:divide-x">
          <div className="p-4 sm:px-5">
            <ZoneTitle>{t('hr.myData.sectionContact')}</ZoneTitle>
            <Row label={t('hr.myData.privateEmail')} value={data.contact.privateEmail} />
            <Row label={t('hr.myData.privatePhone')} value={data.contact.privatePhone} />
            <Row label={t('hr.myData.workEmail')} value={data.contact.workEmail} />
          </div>
          <div className="p-4 sm:px-5">
            <ZoneTitle>{t('hr.myData.sectionAddress')}</ZoneTitle>
            <Row label={t('hr.myData.street')} value={data.address.street} />
            <Row label={t('hr.myData.city')} value={cityLine} />
            <Row label={t('hr.myData.country')} value={data.address.country} />
          </div>
          <div className="p-4 sm:col-span-2 sm:border-t sm:px-5 lg:col-span-1 lg:border-t-0">
            <ZoneTitle>{t('hr.myData.sectionEmployment')}</ZoneTitle>
            <Row label={t('hr.myData.department')} value={data.employment.department} />
            <Row label={t('hr.myData.manager')} value={data.employment.managerName} />
            <Row label={t('hr.myData.taxOffice')} value={data.employment.taxOffice} />
            <Row label={t('hr.myData.nfzBranch')} value={data.employment.nfzBranch} />
          </div>
        </div>

        {/* Sensitive: documents & account */}
        <div className="border-b p-4 sm:px-5">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Lock className="text-muted-foreground size-3.5" />
            <span className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
              {t('hr.myData.sectionSensitive')}
            </span>
            <div className="ml-auto">
              {revealed ? (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">
                    {t('hr.myData.revealedFor', { s: secondsLeft })}
                  </span>
                  <Button variant="outline" size="sm" className="h-7" onClick={hide}>
                    <EyeOff className="size-3.5" />
                    {t('hr.myData.hideNow')}
                  </Button>
                </div>
              ) : data.revealLocked ? (
                <span className="text-xs text-rose-600 dark:text-rose-400">
                  {t('hr.myData.revealLocked')}
                </span>
              ) : data.hasPesel ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7"
                  onClick={() => { setDigits(''); setDialogOpen(true); }}
                >
                  <Eye className="size-3.5" />
                  {t('hr.myData.reveal')}
                </Button>
              ) : (
                <span className="text-muted-foreground text-xs">
                  {t('hr.myData.revealNoPesel')}
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Field
              label={t('hr.myData.pesel')}
              value={revealed ? revealed.pesel : data.sensitive.peselMasked}
            />
            <Field label={t('hr.myData.idDocument')} value={idDocument} />
            <Field
              label={t('hr.myData.iban')}
              value={revealed ? revealed.privateIban : data.sensitive.ibanMasked}
            />
          </div>
        </div>

        {/* Sensitive: payroll as a payslip flow — the structure stays visible
            before the reveal, only the amounts are masked */}
        <div className="p-4 sm:px-5">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Lock className="text-muted-foreground size-3.5" />
            <span className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
              {t('hr.myData.sectionPayroll')}
            </span>
            {revealed && payroll && (
              <span className="text-muted-foreground ml-auto text-xs">
                {t('hr.myData.payrollRate')}: {formatPln(payroll.rate.amount)}
                {payroll.rate.rateType === 'hourly' ? t('hr.myData.perHour') : t('hr.myData.perMonth')}
              </span>
            )}
          </div>
          {revealed && (!payroll || !b) ? (
            <p className="text-muted-foreground text-sm">{t('hr.myData.payrollUnavailable')}</p>
          ) : (
            <div className="flex flex-wrap items-end gap-x-2.5 gap-y-3">
              <PayItem label={t('hr.myData.payrollGross')} value={pay(b?.gross)} />
              <PayOp>−</PayOp>
              <PayItem
                label={t('hr.myData.payrollZusShort')}
                title={t('hr.myData.payrollZus')}
                value={pay(b?.employeeContributions?.total ?? null)}
              />
              <PayOp>−</PayOp>
              <PayItem
                label={t('hr.myData.payrollHealthShort')}
                title={t('hr.myData.payrollHealth')}
                value={pay(b?.health)}
              />
              <PayOp>−</PayOp>
              <PayItem
                label={t('hr.myData.payrollPitShort')}
                title={t('hr.myData.payrollPit')}
                value={pay(b?.pitAdvance)}
              />
              <PayOp>−</PayOp>
              <PayItem
                label={t('hr.myData.payrollPpkShort')}
                title={t('hr.myData.payrollPpk')}
                value={pay(b?.ppkEmployee)}
              />
              <PayOp>=</PayOp>
              <PayItem label={t('hr.myData.payrollNet')} value={pay(b?.net)} emphasis />
              <div className="border-border mx-2 h-8 self-center border-l" />
              <PayItem
                label={t('hr.myData.payrollEmployerCost')}
                value={pay(b?.employerTotalCost)}
              />
            </div>
          )}
        </div>
      </Card>

      <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <Info className="size-3.5 shrink-0" />
        {t('hr.myData.readOnlyHint')}
      </p>

      {/* Reveal dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('hr.myData.revealTitle')}</DialogTitle>
            <DialogDescription>{t('hr.myData.revealHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="pesel-last4">{t('hr.myData.revealInput')}</Label>
            <Input
              id="pesel-last4"
              inputMode="numeric"
              maxLength={4}
              autoComplete="off"
              value={digits}
              onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && /^\d{4}$/.test(digits)) {
                  e.preventDefault();
                  handleReveal();
                }
              }}
            />
          </div>
          <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span>{t('hr.myData.revealLockWarning')}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={busy || !/^\d{4}$/.test(digits)} onClick={handleReveal}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('hr.myData.revealSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
